import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { axios } from '../api';
import { Option } from '../pages/types';
import { BOTTOM_TOAST, mapSntApiDate, SHIPMENT_DISPLAY_VALUE } from '../utils';

export type ShippingRate = {
  id?: string;
  shipping_method_label: string;
  carrier: string;
  service_type: string;
  price: {
    subtotal: number;
    tax: number;
    total: number;
  };
  min_ship_date: string;
  max_ship_date: string;
  min_delivery_date: string;
  max_delivery_date: string;
};

export type ShippingMethodEDD = Partial<ShippingRate>;

export type ShippingRatesResponse = {
  [key: string]: ShippingRate;
};

export type ShippingOption = Option & {
  rate?: ShippingMethodEDD;
};

export const useEstimatedDeliveryDate = (
  accountId: string,
  hasFrozen: boolean,
  hasAmbient: boolean,
  zipCodeOverride?: string
) => {
  const [isLoadingShippingRates, setIsLoadingShippingRates] = useState(true);
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchShippingRates = useCallback(async () => {
    try {
      setIsLoadingShippingRates(true);
      setError(null);

      const response = await axios.get(`/accounts/${accountId}/shipping_dates_by_method/`, {
        params: {
          has_frozen: hasFrozen,
          has_ambient: hasAmbient,
          zip_code_override: zipCodeOverride,
        },
      });

      const shippingRates: ShippingRatesResponse = response.data.shipping_methods;

      const options: ShippingOption[] = Object.entries(shippingRates).map(([key, rate]) => {
        const formattedMinDate = mapSntApiDate(rate.min_delivery_date);
        const formattedMaxDate = mapSntApiDate(rate.max_delivery_date);
        const deliveryDateRange =
          rate.min_delivery_date === rate.max_delivery_date
            ? formattedMinDate
            : `${formattedMinDate} - ${formattedMaxDate}`;
        rate.id = key; // Assign the key as id to the rate object
        return {
          value: key,
          label: `${SHIPMENT_DISPLAY_VALUE[key] || key} (delivery ${deliveryDateRange}) - FREE`,
          rate,
        };
      });

      setShippingOptions(options);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      const errorMessage = 'Failed to load shipping options';
      setError(errorMessage);
      toast.error(errorMessage, BOTTOM_TOAST);

      // Fallback to safe options if API fails
      setShippingOptions([
        { value: 'ECONOMY', label: 'Economy' },
        { value: 'STANDARD', label: 'Standard' },
      ]);
    } finally {
      setIsLoadingShippingRates(false);
    }
  }, [accountId, hasFrozen, hasAmbient, zipCodeOverride]);

  useEffect(() => {
    fetchShippingRates();
  }, [fetchShippingRates]);

  return {
    shippingOptions,
    isLoadingShippingRates,
    error,
    refetch: fetchShippingRates,
  };
};

export const useShippingRates = (orderId: string, chargeDate?: string) => {
  const [isLoadingShippingRates, setIsLoadingShippingRates] = useState(true);
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchShippingRates = useCallback(async () => {
    const effectiveChargeDate: string =
      chargeDate || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

    try {
      setIsLoadingShippingRates(true);
      setError(null);

      const response = await axios.get(`/orders/${orderId}/shipping_rates_with_estimates/`, {
        params: {
          charge_date: effectiveChargeDate,
        },
      });

      const shippingRates: ShippingRatesResponse = response.data;

      const options: ShippingOption[] = Object.entries(shippingRates).map(([key, rate]) => {
        const formattedMinDate = mapSntApiDate(rate.min_delivery_date);
        const formattedMaxDate = mapSntApiDate(rate.max_delivery_date);
        const deliveryDateRange =
          rate.min_delivery_date === rate.max_delivery_date
            ? formattedMinDate
            : `${formattedMinDate} - ${formattedMaxDate}`;
        rate.id = key; // Assign the key as id to the rate object
        return {
          value: key,
          label: `${SHIPMENT_DISPLAY_VALUE[key] || key} (delivery ${deliveryDateRange}) - FREE`,
          rate,
        };
      });

      setShippingOptions(options);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      const errorMessage = 'Failed to load shipping options';
      setError(errorMessage);
      toast.error(errorMessage, BOTTOM_TOAST);

      // Fallback to safe options if API fails
      setShippingOptions([
        { value: 'ECONOMY', label: 'Economy' },
        { value: 'STANDARD', label: 'Standard' },
      ]);
    } finally {
      setIsLoadingShippingRates(false);
    }
  }, [orderId, chargeDate]);

  useEffect(() => {
    fetchShippingRates();
  }, [fetchShippingRates]);

  return {
    shippingOptions,
    isLoadingShippingRates,
    error,
    refetch: fetchShippingRates,
  };
};
